import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TrendingUp, Rocket } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

const roles = [
  { value: "investor", icon: TrendingUp, title: "Investor", desc: "Browse pitches & build your watchlist" },
  { value: "founder", icon: Rocket, title: "Founder", desc: "Raise capital & track engagement" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "investor" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome to EBN, ${user.name}!`);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen flex items-center justify-center pt-24 pb-16 px-4">
      <div className="noise-overlay" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(245,179,1,0.06),transparent_55%)]" />
      <div className="relative w-full max-w-md rounded-2xl bg-[#0d0d0d] border border-white/10 p-8 fade-up" data-testid="signup-card">
        <img src={LOGO_URL} alt="EBN" className="h-12 w-auto mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-center">Join EBN</h1>
        <p className="mt-1 text-sm text-zinc-500 text-center">Choose your role and start today</p>
        <form onSubmit={submit} className="mt-7 space-y-4 text-left">
          <div className="grid grid-cols-2 gap-3">
            {roles.map((r) => (
              <button
                key={r.value}
                type="button"
                data-testid={`signup-role-${r.value}`}
                onClick={() => setForm({ ...form, role: r.value })}
                className={`rounded-xl border p-4 text-left transition-colors ${
                  form.role === r.value ? "border-[#F5B301] bg-[#F5B301]/10" : "border-white/10 hover:border-white/25"
                }`}
              >
                <r.icon size={18} className="text-[#F5B301] mb-2" />
                <p className="text-sm font-semibold text-white">{r.title}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{r.desc}</p>
              </button>
            ))}
          </div>
          <input
            data-testid="signup-name-input"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#F5B301]/50"
          />
          <input
            data-testid="signup-email-input"
            type="email"
            placeholder="Email address"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#F5B301]/50"
          />
          <input
            data-testid="signup-password-input"
            type="password"
            placeholder="Password (min 6 characters)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-4 py-3 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#F5B301]/50"
          />
          {error && <p data-testid="signup-error" className="text-xs text-red-400">{error}</p>}
          <button
            data-testid="signup-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-full bg-[#F5B301] text-black text-sm font-bold hover:bg-[#D99E01] transition-colors disabled:opacity-60"
          >
            {loading ? "Creating account…" : `Sign Up as ${form.role === "founder" ? "Founder" : "Investor"}`}
          </button>
        </form>
        <p className="mt-6 text-xs text-zinc-500 text-center">
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-to-login-link" className="text-[#F5B301] font-semibold hover:underline">Login</Link>
        </p>
      </div>
    </main>
  );
}
